import type React from 'react'
import type {ReactNode} from 'react'
import {useEffect, useRef} from 'react'
import {AccordionItemV2} from 'pepsico-ds'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from 'rehype-raw'
import {SidePanel} from '@/components/GDS/organisms/SidePanel'
import Accordion from '@/components/GDS/organisms/Accordion/Accordion'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Definition {
  /** Accordion header label */
  title: string
  /** Content text — rendered as markdown when `isMarkdown` is true, otherwise plain text */
  info: string
  /** When true, `info` is rendered via ReactMarkdown. Defaults to false. */
  isMarkdown?: boolean
}

export interface DataDefinitionDrawerProps {
  /** Controls visibility of the drawer */
  isOpen: boolean
  /** Callback when the drawer is closed */
  onClose: () => void
  /** Header title displayed at the top of the drawer */
  title?: string
  /** Structured definitions rendered as accordion items */
  definitions?: Definition[]
  /** Freeform content — takes precedence over `definitions` when both are provided */
  children?: ReactNode
  /** Width of the right-side drawer */
  width?: string
  /** Auto-scroll to a definition matching this title */
  scrollToId?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const noop = (): void => {
  /* required by Accordion API */
}

/** Render definition info as plain text (splitting on newlines) or markdown */
const DefinitionContent: React.FC<{info: string; isMarkdown?: boolean}> = ({info, isMarkdown}) => {
  if (isMarkdown) {
    return (
      <div className="text-body-base prose prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]}>
          {info}
        </ReactMarkdown>
      </div>
    )
  }

  return (
    <div className="text-body-base" style={{padding: '0 0 8px 0'}}>
      {info.split('\n').map((line, i) => (
        <p key={`line-${line.slice(0, 20)}-${i}`} style={{margin: 0, marginBottom: 4}}>
          {line}
        </p>
      ))}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

const DataDefinitionDrawer: React.FC<DataDefinitionDrawerProps> = ({
  isOpen,
  onClose,
  title = 'Definitions',
  definitions,
  children,
  width = '420px',
  scrollToId,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to a definition by title when the drawer opens
  useEffect(() => {
    if (!isOpen || !scrollToId || !scrollContainerRef.current) return

    // Small delay to allow the drawer animation to complete
    const timer = setTimeout(() => {
      const target = scrollContainerRef.current?.querySelector(`[data-definition-id="${CSS.escape(scrollToId)}"]`)
      target?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }, 300)

    return () => clearTimeout(timer)
  }, [isOpen, scrollToId])

  return (
    <SidePanel
      showSidePanel={isOpen}
      onCloseSidePanel={onClose}
      title={title}
      position="right"
      type="vertical"
      horizontalSidepanelWidth={width}
      showCloseIcon={true}
    >
      <div
        ref={scrollContainerRef}
        data-testid="data-definition-drawer-content"
        style={{maxHeight: '80vh', overflowY: 'auto'}}
      >
        {children ??
          (definitions && definitions.length > 0 ? (
            <Accordion presentation="no-background" separator={true} onUpdate={noop}>
              {definitions.map(def => (
                <AccordionItemV2
                  key={def.title}
                  label={def.title}
                  presentation="no-background"
                  data-definition-id={def.title}
                >
                  <DefinitionContent info={def.info} isMarkdown={def.isMarkdown} />
                </AccordionItemV2>
              ))}
            </Accordion>
          ) : null)}
      </div>
    </SidePanel>
  )
}

export default DataDefinitionDrawer
